import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * `<nb-icon-button>` — icon-only action button (Mockup `.iconbtn` + `.iconbtn.web`).
 *
 * Renders a real `<button>` so semantics and keyboard behaviour come for free.
 * Project a single `<nb-icon>` via content projection; it is sized to 23px
 * (web variant 21px) through `--nb-icon-size`. An optional `count` shows the
 * `.dot` notification badge in the top-right corner (hidden when 0/undefined).
 *
 * `label` is required and becomes the button's `aria-label`, because the button
 * has no visible text. The badge count is folded into the accessible name so the
 * notification state is never conveyed by colour alone.
 *
 * @example
 * <nb-icon-button label="Varsler" [count]="3"><nb-icon [icon]="Bell"/></nb-icon-button>
 * <nb-icon-button web label="Søk"><nb-icon [icon]="Search"/></nb-icon-button>
 */
@Component({
  selector: 'nb-icon-button',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<button
    class="iconbtn"
    [class.web]="web()"
    [type]="type()"
    [disabled]="disabled()"
    [attr.aria-label]="ariaLabel()"
  >
    <ng-content />
    @if (hasBadge()) {
      <span class="dot" aria-hidden="true">{{ count() }}</span>
    }
  </button>`,
  styleUrl: './icon-button.css',
})
export class NbIconButton {
  /** Required accessible name for the icon-only button. */
  readonly label = input.required<string>();
  /** The smaller 42×42 web variant (Mockup `.iconbtn.web`). */
  readonly web = input(false);
  /** Optional notification count; renders the `.dot` badge when > 0. */
  readonly count = input<number>();
  readonly type = input<'button' | 'submit' | 'reset'>('button');
  readonly disabled = input(false);

  protected hasBadge(): boolean {
    const c = this.count();
    return c != null && c > 0;
  }

  protected ariaLabel(): string {
    return this.hasBadge() ? `${this.label()} (${this.count()})` : this.label();
  }
}
